import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TermsEditor from "@/components/TermsEditor";
import { BadgeCheck, ImagePlus, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const OWNER = "ronick@somcoregroup.com";
const EMPTY = { logo_b64: "", partner_line: "", quote_title: "", terms: [] };

const LogoPicker = ({ value, onChange, readOnly }) => {
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error("Please use an image under 1.5 MB"); return; }
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-16 w-40 items-center justify-center rounded-md border border-dashed border-border bg-slate-50" data-testid="oem-logo-preview">
        {value ? <img src={value} alt="OEM logo" className="max-h-14 max-w-[9.5rem] object-contain" /> : <span className="text-[11px] text-muted-foreground">No Logo</span>}
      </div>
      {!readOnly && (
        <label className="inline-flex cursor-pointer items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-slate-50" data-testid="oem-logo-upload">
          <ImagePlus className="mr-1.5 h-4 w-4" />{value ? "Replace Logo" : "Upload Logo"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="oem-logo-input" />
        </label>
      )}
    </div>
  );
};

export const OemQuoteProfileCard = ({ supplierId, supplierName }) => {
  const { user } = useAuth();
  const isOwner = (user?.email || "").toLowerCase() === OWNER;
  const [p, setP] = useState(EMPTY);
  const [meta, setMeta] = useState(null);
  const [standard, setStandard] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/quote-terms/standard").then((r) => setStandard(r.data)).catch(() => {});
    api.get(`/suppliers/${supplierId}/quote-profile`).then((r) => { if (r.data) { setP({ ...EMPTY, ...r.data }); setMeta(r.data); } }).catch(() => {});
  }, [supplierId]);

  const set = (patch) => setP((x) => ({ ...x, ...patch }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/suppliers/${supplierId}/quote-profile`, p);
      setMeta(data);
      toast.success("OEM quote branding approved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-border bg-white p-6" data-testid="oem-quote-profile">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-sm font-bold tracking-tight">Client Quote Branding & Terms</h3>
        {meta?.updated_at && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700" data-testid="oem-profile-approved"><BadgeCheck className="h-3.5 w-3.5" />Approved by {meta.updated_by_name}</span>}
        {!isOwner && <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" />Only Ronick can change this</span>}
      </div>
      <p className="mb-4 text-xs text-muted-foreground">When you tick "Quote As {supplierName}" on a client quote, this logo prints beside Somcore's and the clauses marked OEM Wording replace the standard ones.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">OEM Logo</Label><LogoPicker value={p.logo_b64} onChange={(v) => set({ logo_b64: v })} readOnly={!isOwner} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Partner Line (Under The Logos)</Label>
          <Input readOnly={!isOwner} placeholder={`Authorised Partner - ${supplierName}`} value={p.partner_line} onChange={(e) => set({ partner_line: e.target.value })} data-testid="oem-partner-line" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Quote Title (After "QUOTATION –")</Label>
          <Input readOnly={!isOwner} placeholder="e.g. FS-CURTIS AIR COMPRESSORS & COMPRESSED AIR EQUIPMENT" value={p.quote_title} onChange={(e) => set({ quote_title: e.target.value })} data-testid="oem-quote-title" /></div>
      </div>
      <div className="mt-5"><Label className="text-xs">Terms & Conditions · Tick A Clause To Replace It With OEM Wording</Label>
        <div className="mt-2"><TermsEditor standard={standard} value={p.terms} onChange={(t) => set({ terms: t })} readOnly={!isOwner} /></div></div>
      {isOwner && (
        <div className="mt-4 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="oem-profile-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BadgeCheck className="mr-2 h-4 w-4" />}Approve & Save</Button>
        </div>
      )}
    </div>
  );
};

export default OemQuoteProfileCard;
